/**
 * Decodes a base64 encoded Unicode string.
 *
 * @param {string} str - The base64 encoded Unicode string to decode
 * @return {string} The decoded Unicode string
 */
function base64DecodeUnicode(str) {
  try {
    return decodeURIComponent(atob(str).replace(/(.)/g, function (m, p) {
      /* eslint-disable-next-line unicorn/prefer-code-point
          -- Since atob gives latin1 characters, using codeAtPoint has the same result as
             charCodeAt, and charCodeAt has better support */
      var code = p.charCodeAt(0).toString(16).toUpperCase();
      if (code.length < 2) {
        code = "0".concat(code);
      }
      return "%".concat(code);
    }));
  } catch (_unused) {
    return atob(str);
  }
}

/**
 * Decodes a base64 URL string into its original form.
 *
 * @param {string} base64Url - The base64 URL string to be decoded.
 * @return {string} The original form of the base64 URL string.
 */
function base64UrlDecode(base64Url) {
  return base64DecodeUnicode(base64Url.replace(/-/g, '+').replace(/_/g, '/'));
}

/**
 * Decode a JWT token and return the decoded JSON object.
 *
 * @param {string} jwtToken - The JWT token to be decoded
 * @return {object} The decoded JSON object
 */
function jwtDecode(jwtToken) {
  return JSON.parse(base64UrlDecode(jwtToken.split('.')[1]));
}

/**
 * Determines if a JWT token is expired based on the provided parameters.
 *
 * @param {string} jwtToken - The JWT token to check for expiration.
 * @param {object} [options] - An object containing optional parameters:
 * @param {number} [options.secondsBefore] - The number of seconds to consider before the actual
 * expiration time. Default is 0.
 * @param {number} [options.issuedAt] - The local timestamp of when the token was issued. When
 * provided the expiration is calculated based on the elapsed time since the token was issued using
 * the iat (issued at) field. If not provided, the expiration time in the token is compared with
 * the current time. This avoids issues when the clock on the client and the server are not in sync.
 * @return {boolean} Returns true if the token is expired, otherwise returns false.
 */
function isTokenExpired(jwtToken) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$secondsBefore = _ref.secondsBefore,
    secondsBefore = _ref$secondsBefore === void 0 ? 0 : _ref$secondsBefore,
    _ref$issuedAt = _ref.issuedAt,
    issuedAt = _ref$issuedAt === void 0 ? 0 : _ref$issuedAt;
  if (!jwtToken) return false;
  var _jwtDecode = jwtDecode(jwtToken),
    exp = _jwtDecode.exp,
    iat = _jwtDecode.iat;
  var elapsedSeconds = Math.trunc(Date.now() / 1000) - Math.trunc(issuedAt / 1000);
  return elapsedSeconds + secondsBefore >= exp - (issuedAt ? iat : 0);
}

export { base64DecodeUnicode, base64UrlDecode, isTokenExpired, jwtDecode };
//# sourceMappingURL=jwt.js.map
